import { useEffect, useState, type ReactNode } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, XCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@baluu/ui/components/tabs";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { hydrateSession, useAuthSnapshot } from "~/widgets/lib/auth-client";
import { formatMoney } from "../money";
import { authInit } from "./reschedule/request";
import type { BundleBooking, BundleCourse, ManageBundlePage, ManageSchool } from "./bundle-manage/types";
import { bundleSpots, bundleState, formatDay, splitBookings, type BundleState } from "./bundle-manage/bundleManage";
import { displayTimezone } from "../timezones";
import {
  ORDER_POLL_LIMIT,
  ORDER_POLL_MS,
  orderOutcome,
  pollAttempts,
  watchOrder,
  type OrderOutcome,
  type OrderPollState,
  type OrderStatus,
} from "./bundle-manage/redemption";
import { BundleHeader, BundleInformation, SpotsCard } from "./bundle-manage/BundleSidebar";
import { PastBookings, UpcomingBookings } from "./bundle-manage/BookingLists";
import { ServicesTab } from "./bundle-manage/ServicesTab";
import { TimetableTab } from "./bundle-manage/TimetableTab";
import { RedeemOverlay, type PlacedOrder, type RedeemRequest } from "./bundle-manage/RedeemOverlay";

// /bundles/manage/<bundle-id>: a customer's bundle. A port of v1's bundle
// page (ManageBundleView and its tabs): the bundle, its spots and expiry,
// the bookings made with it (each linking to its manage page), the listings
// it can be used on, and the timetable to book sessions with it. Booking
// goes through the shared checkout in bundle mode (Services) or straight
// from the timetable's selection, and ends at the order v1's basket checkout
// sends when a bundle pays (POST /widget/app/confirmFreeBasketOrder).
// The page itself needs no sign-in: the bundle's id in the link is the key.

const pageKey = (api: string, bundleId: string) => ["booking", "bundle-manage", api, bundleId];

export function BundleManageView({ ctx, bundleId }: { ctx: ViewContext; bundleId: string }) {
  const { token } = useAuthSnapshot();

  useEffect(() => {
    void hydrateSession(ctx.api, ctx.businessSlug || undefined);
  }, [ctx.api, ctx.businessSlug]);

  const page = useQuery({
    // The bearer rides along, so the answer is per token (the prefix is
    // what invalidation matches).
    queryKey: [...pageKey(ctx.api, bundleId), token],
    queryFn: () =>
      bookingJson<ManageBundlePage>(`${ctx.api}/widget/pages/bundles/manage/${encodeURIComponent(bundleId)}`, authInit(token)),
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
  });

  const isMarketplace =
    page.data?.bundle?.source === "obby" && !isOtherSchool(ctx.businessId, page.data.school, page.data.bundle.businessID);
  useEffect(() => {
    if (isMarketplace) ctx.toMarketplace(`/bundles/manage/${bundleId}`);
  }, [isMarketplace, ctx, bundleId]);

  if (page.isLoading || isMarketplace) return <BookingLoading rows={4} />;
  const school = page.data?.school;
  // Another school's bundle isn't this site's to show.
  const elsewhere = isOtherSchool(ctx.businessId, school, page.data?.bundle?.businessID);
  if (page.isError || !page.data?.bundle || !school || elsewhere) {
    const status = page.error instanceof BookingApiError ? page.error.status : page.isError ? 0 : 404;
    return status === 404 || elsewhere || (page.isSuccess && (!page.data?.bundle || !school)) ? (
      <RecordNotFound what="bundle" />
    ) : (
      <BookingProblem
        title="We couldn’t load this bundle"
        message={messageOf(page.error, "Please try again in a moment.")}
        onRetry={() => page.refetch()}
      />
    );
  }

  return <BundleManage ctx={ctx} data={page.data} school={school} />;
}

function BundleManage({ ctx, data, school }: { ctx: ViewContext; data: ManageBundlePage; school: ManageSchool }) {
  const qc = useQueryClient();
  const { bundle } = data;
  const courses = data.availableCourses ?? [];
  // An API that predates the flag shows the tab rather than hide one that
  // has sessions (as v1 does).
  const showTimetable = data.hasTimetableSessions ?? true;
  const [now] = useState(() => Date.now());
  const [tab, setTab] = useState(showTimetable ? "timetable" : "services");
  const [request, setRequest] = useState<RedeemRequest | null>(null);
  const [orders, setOrders] = useState<PlacedOrder[]>([]);
  const [timetableRound, setTimetableRound] = useState(0);

  const orderKey = (id: string) => ["booking", "bundle-order", ctx.api, id];
  const refresh = () => {
    // The bundle's spots and bookings, and the sessions' places, moved.
    void qc.invalidateQueries({ queryKey: pageKey(ctx.api, bundle.id) });
    void qc.invalidateQueries({ queryKey: ["booking", "bundle-timetable"] });
  };
  const statuses = useQueries({
    queries: orders.map((order) => ({
      queryKey: orderKey(order.id),
      queryFn: async () => {
        // This check's number, failed checks included (each poll is one
        // attempt: no retries inside it).
        const attempt = pollAttempts(qc.getQueryState<OrderStatus>(orderKey(order.id))) + 1;
        let status: OrderStatus | undefined;
        try {
          status = await bookingJson<OrderStatus>(`${ctx.api}/widget/app/basketOrderStatus/${encodeURIComponent(order.id)}`);
          return status;
        } finally {
          // Done, or the last check: read the bundle again either way.
          if ((status && orderOutcome(status) !== "pending") || attempt >= ORDER_POLL_LIMIT) refresh();
        }
      },
      refetchInterval: (query: { state: OrderPollState }) => (watchOrder(query.state).poll ? ORDER_POLL_MS : false),
      retry: false,
      staleTime: 0,
    })),
  });
  const outcomes: Array<{ order: PlacedOrder; outcome: OrderOutcome; stalled: boolean }> = orders.map((order, i) => {
    // The cache's counters (the result has no data count); `statuses`
    // re-renders this on every check.
    const counters = qc.getQueryState<OrderStatus>(orderKey(order.id));
    const watch = watchOrder({ data: statuses[i]?.data, dataUpdateCount: counters?.dataUpdateCount, errorUpdateCount: counters?.errorUpdateCount });
    return { order, outcome: watch.outcome, stalled: watch.stalled };
  });
  // A stalled order no longer holds spots back: the page has read the
  // bundle again by then, and shows what it says.
  const pending = outcomes.filter((o) => o.outcome === "pending" && !o.stalled).map((o) => o.order);
  const spots = bundleSpots(bundle, pending.reduce((sum, o) => sum + o.spots, 0));
  const state = bundleState(bundle, now);
  const { upcoming, past } = splitBookings(data.bookingHistory ?? [], now);
  const currency = school.preferredCurrency || "GBP";
  const money = (pence: number) => formatMoney(pence, currency);
  const schoolZone = displayTimezone(school.timezone);
  const zoneOf = (booking: BundleBooking) => displayTimezone(booking.course?.timezone, school.timezone);
  const canBook = !state.closed && spots.remaining > 0;

  const placed = (order: PlacedOrder) => {
    setOrders((list) => [...list, order]);
    setRequest(null);
    // The timetable starts over: its selection is booked.
    setTimetableRound((n) => n + 1);
    setTab("upcoming");
  };
  const dismiss = (id: string) => setOrders((list) => list.filter((o) => o.id !== id));

  return (
    <div className="space-y-6">
      {state.cancelled && (
        <Alert tone="orange" icon={<XCircle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" aria-hidden />} title="Bundle cancelled">
          This bundle has been cancelled and you cannot make new bookings with it. Your existing bookings will still take place as scheduled.
        </Alert>
      )}
      {!state.cancelled && state.expired && (
        <Alert tone="orange" icon={<XCircle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" aria-hidden />} title="Bundle expired">
          This bundle expired on {formatDay(bundle.expiredDate, schoolZone)}, so it can’t be used for new bookings. Your existing bookings
          will still take place as scheduled.
        </Alert>
      )}
      {state.refunded && (
        <Alert tone="blue" icon={<AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" aria-hidden />} title="Bundle refunded">
          <p>A {bundle.refundType || "full"} refund has been processed for this bundle.</p>
          {!!bundle.refundedAmount && (
            <p>
              <strong>Refund amount:</strong> {money(bundle.refundedAmount)}
            </p>
          )}
          {bundle.refundedDate && (
            <p>
              <strong>Refund date:</strong> {formatDay(bundle.refundedDate, schoolZone)}
            </p>
          )}
          <p className="mt-2 text-xs">The refund will appear in your original payment method within 5-10 business days.</p>
        </Alert>
      )}

      {outcomes.map(({ order, outcome, stalled }) => (
        <OrderNotice key={order.id} order={order} outcome={outcome} stalled={stalled} onDismiss={() => dismiss(order.id)} />
      ))}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <div className="rounded-xl border bg-card p-6 shadow-sm">
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <div className="mb-6">
                <h2 className="mb-1 text-xl font-semibold">
                  Manage your bundle ({spots.used}/{spots.total})
                </h2>
                <p className="text-sm text-muted-foreground">
                  {showTimetable ? "Book sessions, view timetable, and track your bookings" : "Book sessions and track your bookings"}
                </p>
              </div>
              <TabsList className={`mb-4 grid h-auto w-full gap-2 sm:w-auto ${showTimetable ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
                <TabsTrigger value="services" className="text-xs sm:text-sm">
                  Services available
                </TabsTrigger>
                {showTimetable && (
                  <TabsTrigger value="timetable" className="text-xs sm:text-sm">
                    Timetable sessions
                  </TabsTrigger>
                )}
                <TabsTrigger value="upcoming" className="text-xs sm:text-sm">
                  Upcoming bookings
                </TabsTrigger>
                <TabsTrigger value="past" className="text-xs sm:text-sm">
                  Past bookings
                </TabsTrigger>
              </TabsList>

              <TabsContent value="services" className="space-y-4">
                {state.closed ? (
                  <Unavailable state={state} />
                ) : (
                  <ServicesTab
                    courses={courses}
                    tutors={data.tutors}
                    canBook={canBook}
                    onBook={(course: BundleCourse) => setRequest({ kind: "course", course })}
                  />
                )}
              </TabsContent>
              {showTimetable && (
                <TabsContent value="timetable" className="space-y-4">
                  {state.closed ? (
                    <Unavailable state={state} />
                  ) : (
                    <TimetableTab
                      key={timetableRound}
                      api={ctx.api}
                      school={school}
                      bundle={bundle}
                      courseIds={courses.map((c) => c._id)}
                      remaining={spots.remaining}
                      upcoming={upcoming}
                      past={past}
                      locations={data.locations}
                      tutors={data.tutors}
                      onBook={(booking) => setRequest({ kind: "items", ...booking })}
                    />
                  )}
                </TabsContent>
              )}
              <TabsContent value="upcoming" className="space-y-4">
                <UpcomingBookings
                  bookings={upcoming}
                  locations={data.locations}
                  tutors={data.tutors}
                  links={ctx.links}
                  timezoneFor={zoneOf}
                  now={now}
                />
              </TabsContent>
              <TabsContent value="past" className="space-y-4">
                <PastBookings bookings={past} locations={data.locations} tutors={data.tutors} links={ctx.links} timezoneFor={zoneOf} now={now} />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:col-span-1">
          <BundleHeader bundle={bundle} state={state} />
          <SpotsCard spots={spots} state={state} pending={pending} />
          <BundleInformation bundle={bundle} spots={spots} timezone={schoolZone} money={money} />
        </aside>
      </div>

      {request && (
        <RedeemOverlay
          api={ctx.api}
          school={school}
          bundle={bundle}
          request={request}
          primaryColor={ctx.primaryColor}
          termsHref={ctx.links.terms()}
          onClose={() => setRequest(null)}
          onPlaced={placed}
        />
      )}
    </div>
  );
}

function Alert({ tone, icon, title, children }: { tone: "orange" | "blue"; icon: ReactNode; title: string; children: ReactNode }) {
  const tones = {
    orange: "border-orange-200 bg-orange-50 text-orange-900",
    blue: "border-blue-200 bg-blue-50 text-blue-900",
  } as const;
  return (
    <div role="status" className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="flex items-start gap-3">
        {icon}
        <div className="flex-1">
          <h3 className="mb-1 font-semibold">{title}</h3>
          <div className="space-y-1 text-sm">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Unavailable({ state }: { state: BundleState }) {
  return (
    <div className="py-12 text-center">
      <XCircle className="mx-auto mb-4 h-12 w-12 text-orange-500" aria-hidden />
      <h3 className="mb-2 text-lg font-semibold">Booking not available</h3>
      <p className="text-muted-foreground">
        {state.cancelled ? "This bundle has been cancelled and new bookings cannot be made." : "This bundle has expired and new bookings cannot be made."}
      </p>
    </div>
  );
}

function OrderNotice({
  order,
  outcome,
  stalled,
  onDismiss,
}: {
  order: PlacedOrder;
  outcome: OrderOutcome;
  stalled: boolean;
  onDismiss: () => void;
}) {
  const what = `${order.title} (${order.spots} spot${order.spots !== 1 ? "s" : ""})`;
  const dismiss = (
    <button type="button" className="ml-2 font-medium underline" onClick={onDismiss}>
      Dismiss
    </button>
  );
  if (outcome === "confirmed") {
    return (
      <Notice tone="success">
        Booking confirmed with your bundle: {what}. We’ve emailed you the details.{dismiss}
      </Notice>
    );
  }
  if (outcome === "partial") {
    return (
      <Notice tone="error">
        Only part of your booking went through: {what}. Check your upcoming bookings, and contact us about the rest.{dismiss}
      </Notice>
    );
  }
  if (outcome === "failed") {
    return (
      <Notice tone="error">
        We couldn’t make your booking: {what}. Your bundle wasn’t used. Please try again or contact us.{dismiss}
      </Notice>
    );
  }
  return (
    <Notice tone="info">
      {stalled
        ? `Your booking is still being made: ${what}. We’ll email you when it’s confirmed.`
        : `Booking with your bundle: ${what}…`}
    </Notice>
  );
}
